import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../shared';

// Form Input Component with Real-time Validation
const FormInput = ({ 
  label, 
  type = 'text', 
  name, 
  value, 
  onChange, 
  placeholder, 
  required, 
  validate,
  rows
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (touched && value.length > 0 && validate) {
      setIsValid(validate(value));
    } else if (value.length === 0) {
      setIsValid(null);
    }
  }, [value, touched, validate]);

  const inputClasses = `w-full px-4 py-3 bg-dark-base rounded-xl text-white placeholder-gray-500 transition-all focus:outline-none ${
    isValid === null
      ? isFocused 
        ? 'border-2 border-neon-orange shadow-[0_0_15px_rgba(253,128,46,0.2)]' 
        : 'border-2 border-neon-orange/20'
      : isValid
        ? 'border-2 border-green-500 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
        : 'border-2 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
  }`;

  const InputElement = rows ? 'textarea' : 'input';

  return (
    <div className="relative">
      <label className="block text-white font-semibold mb-2">{label}</label>
      <div className="relative">
        <InputElement
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); setTouched(true); }}
          className={inputClasses}
          placeholder={placeholder}
          required={required}
          rows={rows}
          style={rows ? { resize: 'none' } : {}}
        />
        
        {/* Validation Icon */}
        {touched && isValid !== null && (
          <motion.div 
            className="absolute right-4 top-1/2 -translate-y-1/2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            {isValid ? (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </motion.div>
        )}
      </div>
      
      {/* Error message */}
      {touched && isValid === false && (
        <motion.p 
          className="text-red-400 text-sm mt-1"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {type === 'email' ? 'Please enter a valid email' : `Please enter a valid ${label.toLowerCase()}`}
        </motion.p>
      )}
    </div>
  );
};

// Loading Spinner
const LoadingSpinner = () => (
  <motion.div
    className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
  />
);

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <motion.div
      className="border border-neon-orange/20 rounded-xl overflow-hidden bg-dark-base/30"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <button
        onClick={onClick}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-dark-base/50 transition-colors"
      >
        <span className="text-white font-semibold pr-4">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0"
        >
          <svg className="w-5 h-5 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 text-gray-400 border-t border-neon-orange/10 pt-3">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// FAQ Section Component
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How long does it take to get started?",
      answer: "You can get started in under 5 minutes! Simply sign up, connect your audio source, and our AI will begin processing immediately. No complex setup required."
    },
    {
      question: "What audio formats are supported?",
      answer: "We support all major audio formats including WAV, MP3, FLAC, AAC, OGG, and AIFF. Our AI automatically detects and optimizes for each format."
    },
    {
      question: "Can I integrate with my existing tools?",
      answer: "Yes! SoundFlowAI integrates seamlessly with popular DAWs, cloud storage services, and content platforms. Check our Integrations section for the full list."
    },
    {
      question: "Is my audio data secure?",
      answer: "Absolutely. We use enterprise-grade encryption (AES-256) for all audio files. Your data is never shared or used for training without explicit consent."
    },
    {
      question: "Do you offer a free trial?",
      answer: "Yes! We offer a 14-day free trial with full access to all features. No credit card required to start."
    },
    {
      question: "How does billing work?",
      answer: "We offer flexible monthly and annual plans. Annual subscriptions include a 20% discount. You can upgrade, downgrade, or cancel anytime."
    }
  ];

  return (
    <motion.div
      className="mt-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <h3 className="text-xl font-heading font-bold text-white mb-6 flex items-center gap-2">
        <svg className="w-6 h-6 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Frequently Asked Questions
      </h3>
      
      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Map Component with Neon Pins
const MapSection = () => {
  return (
    <div className="h-full min-h-[400px] bg-dark-base rounded-2xl overflow-hidden relative border border-neon-orange/20">
      {/* Embedded Map (Google Maps iframe) */}
      <iframe
        title="VeritoneX Location"
        src="https://www.google.com/maps?q=777+S+Figueroa+St,+Los+Angeles,+CA+90017&output=embed"
        className="absolute inset-0 w-full h-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        aria-hidden="false"
      />
      {/* Map Overlay to tint the iframe */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-base/40 to-dark-base/80" />
      
      {/* Neon Pin Marker */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="relative">
          <motion.div
            className="w-8 h-8 rounded-full bg-neon-orange flex items-center justify-center"
            animate={{
              boxShadow: [
                '0 0 20px rgba(253, 128, 46, 0.5)',
                '0 0 40px rgba(253, 128, 46, 0.8)',
                '0 0 20px rgba(253, 128, 46, 0.5)',
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
            </svg>
          </motion.div>
          
          {/* Ripple effect */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-neon-orange"
            animate={{ scale: [1, 2, 2], opacity: [1, 0.5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>

      {/* Address Card */}
      <div className="absolute bottom-6 left-6 right-6 bg-dark-deeper/90 backdrop-blur rounded-xl p-4 border border-neon-orange/20">
        <h4 className="text-white font-heading font-bold mb-2">Connect With Us</h4>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <svg className="w-4 h-4 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>777 S Figueroa St, Los Angeles, CA 90017, United States</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <svg className="w-4 h-4 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>support@veritonetech.com</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <svg className="w-4 h-4 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>+1 (213) 659-4728</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Form
const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const TURNSTILE_SITE_KEY = '0x4AAAAAACsnMZC6eN07CLKw';

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validateName = (name) => name.trim().length >= 2;
  const validateMessage = (msg) => msg.trim().length >= 10;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isFormValid = 
    validateName(formData.name) && 
    validateEmail(formData.email) && 
    validateMessage(formData.message);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    
    setIsSubmitting(true);
    try {
      // read Turnstile token inserted by widget
      const tokenInput = document.querySelector('input[name="cf-turnstile-response"]');
      const turnstileToken = tokenInput?.value || '';

      const res = await fetch('https://formspree.io/f/mdawynrq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          'cf-turnstile-response': turnstileToken,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error('Formspree error', data);
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      // reset Turnstile widget and token if available
      try {
        if (window.turnstile) window.turnstile.reset();
        if (tokenInput) tokenInput.value = '';
      } catch (err) {
        // ignore
      }
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error(err);
      // Minimal UX: alert on failure. Could be replaced with inline error state.
      alert('Unable to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load Turnstile script once
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
    return () => {
      // keep script; no removal to avoid reloading on navigation
    };
  }, []);

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="bg-dark-base/50 rounded-2xl p-6 md:p-8 border border-neon-orange/10"
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
    >
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            className="flex flex-col items-center justify-center py-12 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <motion.div
              className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 0.5 }}
            >
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <h3 className="text-xl font-heading font-bold text-white mb-2">Message Sent!</h3>
            <p className="text-gray-400">We'll get back to you within 24 hours.</p>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            className="space-y-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="grid md:grid-cols-2 gap-5">
              <FormInput
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                validate={validateName}
              />
              <FormInput
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                validate={validateEmail}
              />
            </div>
            
            <FormInput
              label="Subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="How can we help?"
            />
            
            <FormInput
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project..."
              required
              validate={validateMessage}
              rows={4}
            />
            {/* Cloudflare Turnstile widget (token will be placed into a hidden input `cf-turnstile-response`) */}
            <div className="mt-4">
              <div
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-theme="dark"
              />
            </div>
            
            <motion.button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                isFormValid && !isSubmitting
                  ? 'bg-neon-orange text-white hover:shadow-neon' 
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
              whileHover={isFormValid && !isSubmitting ? { scale: 1.01 } : {}}
              whileTap={isFormValid && !isSubmitting ? { scale: 0.99 } : {}}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner />
                  Sending...
                </>
              ) : (
                <>
                  Send Message
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.form>
  );
};

const ContactSection = () => {
  return (
    <Section id="contact" className="bg-dark-deeper relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-0 right-1/4 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.3, 0.2] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <motion.span 
            className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Contact Us
          </motion.span>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Let's Start a{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
              Conversation
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Have questions or want to schedule a demo? Fill valid fields to enable sending.
          </motion.p>
        </div>

        {/* Split Layout: Form (left) + Map (right) */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form (full width on mobile) */}
          <div className="order-1">
            <ContactForm />
          </div>
          
          {/* Map / Address (below on mobile) */}
          <motion.div 
            className="order-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <MapSection />
          </motion.div>
        </div>

        {/* FAQ Section */}
        <FAQSection />
      </div>
    </Section>
  );
};

export default ContactSection;
